import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Icon from 'assets/components/icon/Icon';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { EffectTypes } from 'models/enums/Automation';
import { useState } from 'react';
import {
  AutomationEffectMetadata,
  AutomationRuleLike,
  AutomationRuleSchema,
  getAutomationEffectMetadataKey,
} from '../helpers/AutomationTypes';
import {
  getEffectByPropertyPath,
  getPathIdEffectValues,
  isEffectPropertyPathMissing,
  isEffectValueMissing,
  isPathIdEffectValueMissing,
  updateEffect,
  updatePathIdEffects,
} from '../helpers/ruleHelpers';
import AutomationClaimAlertModal from './form/Automation.ClaimAlert.Modal';

type Props = {
  rule: AutomationRuleLike;
  effect: AutomationEffectMetadata;
  effectIndex?: number;
  schema?: AutomationRuleSchema;
  readonly?: boolean;
  isPropertySelectorDisabled?: boolean;
  onChange(rule: AutomationRuleLike): void;
};

const claimAlertEffectPropertyPath = 'ClaimJob.Claim.ClaimAlerts';

function isClaimAlertEffectValue(value: any) {
  if (!value || typeof value !== 'string') return false;

  try {
    const parsed = JSON.parse(value);
    return Boolean(parsed && typeof parsed === 'object' && parsed.type && parsed.title && parsed.message);
  } catch {
    return false;
  }
}

function getSupportedEffectTypes(schema: AutomationRuleSchema | undefined, effect: AutomationEffectMetadata) {
  if (effect.type === 'assign' && effect.supportedEffectTypes?.length) return effect.supportedEffectTypes;
  return schema?.supportedEffectTypes?.length ? schema.supportedEffectTypes : [EffectTypes.Assign];
}

function getEffectTypeOptions(schema: AutomationRuleSchema | undefined, effect: AutomationEffectMetadata) {
  const sourceEffects = schema?.effects?.length ? schema.effects : [effect];
  const availableTypes = new Set<Model.Enum.Automation.EffectTypes>();

  sourceEffects.forEach((item) => {
    getSupportedEffectTypes(schema, item).forEach((type) => availableTypes.add(type));
  });

  if (schema?.supportedEffectTypes?.length) {
    return schema.supportedEffectTypes.filter((type) => availableTypes.has(type));
  }

  return Array.from(availableTypes);
}

function getEffectPropertyOptions(
  schema: AutomationRuleSchema | undefined,
  effect: AutomationEffectMetadata,
  effectType?: Model.Enum.Automation.EffectTypes
) {
  const sourceEffects = schema?.effects?.length ? schema.effects : [effect];
  const filteredEffects = effectType
    ? sourceEffects.filter((item) => getSupportedEffectTypes(schema, item).includes(effectType))
    : sourceEffects;

  return filteredEffects.map((item) => {
    const propertyPath = getAutomationEffectMetadataKey(item);
    return {
      id: propertyPath,
      title: propertyPath,
      htmlElementProps: { title: propertyPath },
    };
  });
}

function getSchemaPropertyOptions(schema: AutomationRuleSchema | undefined) {
  return Object.keys(schema?.properties || {}).map((propertyPath) => {
    return {
      id: propertyPath,
      title: propertyPath,
      htmlElementProps: { title: propertyPath },
    };
  });
}

export default function AutomationEffectEditor(props: Props) {
  const { lang, translate } = getLocales();
  const availableEffects = props.schema?.effects?.length ? props.schema.effects : [props.effect];
  const allEffectPropertyOptions = getEffectPropertyOptions(props.schema, props.effect);
  const sourcePropertyOptions = getSchemaPropertyOptions(props.schema);
  const storedEffect = typeof props.effectIndex === 'number' ? props.rule.effects?.[props.effectIndex] : undefined;
  const storedEffectMetadata =
    storedEffect &&
    availableEffects.find(
      (effect) => effect.type === 'assign' && getAutomationEffectMetadataKey(effect) === storedEffect.propertyPath
    );
  const selectedEffect = storedEffectMetadata || props.effect;
  const selectedEffectPropertyKey = getAutomationEffectMetadataKey(selectedEffect);
  const supportedEffectTypes = getSupportedEffectTypes(props.schema, selectedEffect);
  const isClaimAlertEffect =
    selectedEffect.type === 'assign' && selectedEffect.propertyPath === claimAlertEffectPropertyPath;
  const allEffectTypeOptions = getEffectTypeOptions(props.schema, props.effect);
  const effectTypeOptions = isClaimAlertEffect ? supportedEffectTypes : allEffectTypeOptions;
  const selectedEffectType =
    storedEffect?.type && effectTypeOptions.includes(storedEffect.type)
      ? storedEffect.type
      : supportedEffectTypes.find((type) => effectTypeOptions.includes(type)) ||
        effectTypeOptions[0] ||
        EffectTypes.Assign;
  const effectPropertyOptions =
    selectedEffectType === EffectTypes.Assign
      ? allEffectPropertyOptions
      : getEffectPropertyOptions(props.schema, props.effect, selectedEffectType);
  const showEffectTypeSelector = selectedEffect.type === 'assign' && effectTypeOptions.length > 1;
  const isEffectTypeSelectorDisabled = props.readonly || effectTypeOptions.length <= 1;
  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Theme.Size.S,
    flexWrap: 'nowrap' as const,
  };
  const inputStyle = {
    flex: '1 1 0',
    minWidth: 0,
    width: 'auto',
  };
  const currentEffect =
    selectedEffect.type === 'assign'
      ? storedEffect || getEffectByPropertyPath(props.rule, selectedEffect.propertyPath)
      : storedEffect;
  const assignValue = currentEffect?.value;
  const hasClaimAlertValue = isClaimAlertEffectValue(assignValue);
  const claimAlertButtonTitle = hasClaimAlertValue ? lang.editAlert : lang.addAlert;
  const claimAlertButtonIcon = hasClaimAlertValue ? 'fas-edit' : 'fas-plus';
  const [isClaimAlertModalOpen, setIsClaimAlertModalOpen] = useState(false);
  const saveClaimAlert = (value: string) => {
    props.onChange(
      updateEffect(props.rule, props.effectIndex, {
        type: EffectTypes.AddOrUpdate,
        propertyPath: claimAlertEffectPropertyPath,
        sourcePropertyPath: undefined,
        value,
      })
    );
    setIsClaimAlertModalOpen(false);
  };

  if (selectedEffect.type === 'pathIdMultiSelect') {
    const requiredError = isPathIdEffectValueMissing(props.rule, selectedEffect) ? lang.required : undefined;
    const propertyPathError = isEffectPropertyPathMissing(selectedEffect) ? lang.required : undefined;

    return (
      <div style={rowStyle}>
        <SelectInput
          name="propertyPath"
          style={inputStyle}
          data={allEffectPropertyOptions}
          value={selectedEffectPropertyKey}
          error={propertyPathError}
          readOnly={true}
          disabled={true}
          onChange={({ propertyPath }) => {
            const nextEffect = availableEffects.find(
              (effect) => effect.type === 'assign' && getAutomationEffectMetadataKey(effect) === propertyPath
            );
            if (!nextEffect || nextEffect.type !== 'assign') return;

            props.onChange(
              updateEffect(props.rule, props.effectIndex, {
                propertyPath: nextEffect.propertyPath,
                value: nextEffect.options?.[0]?.id?.toString() ?? '',
              })
            );
          }}
        />
        <SelectInput
          name="selectedEffectIds"
          style={inputStyle}
          isMultiselect
          data={selectedEffect.options || []}
          value={getPathIdEffectValues(props.rule, selectedEffect)}
          error={requiredError}
          readOnly={props.readonly}
          disabled={props.readonly}
          onChange={({ selectedEffectIds }) =>
            props.onChange(updatePathIdEffects(props.rule, selectedEffect, selectedEffectIds || []))
          }
        />
      </div>
    );
  }
  const requiredError = isEffectValueMissing(currentEffect) ? lang.required : undefined;
  const propertyPathError = isEffectPropertyPathMissing(selectedEffect, currentEffect) ? lang.required : undefined;
  const defaultAssignValue = selectedEffect.options?.[0]?.id?.toString() ?? '';
  const defaultSourcePropertyPath = sourcePropertyOptions[0]?.id?.toString() || '';
  const hasAssignOptions = Boolean(selectedEffect.options?.length);
  const getCompatibleEffect = (type: Model.Enum.Automation.EffectTypes) => {
    if (selectedEffect.type === 'assign' && getSupportedEffectTypes(props.schema, selectedEffect).includes(type)) {
      return selectedEffect;
    }

    return (
      availableEffects.find(
        (effect) => effect.type === 'assign' && getSupportedEffectTypes(props.schema, effect).includes(type)
      ) || selectedEffect
    );
  };
  const onSelectEffectType = (type: Model.Enum.Automation.EffectTypes) => {
    const nextEffect = getCompatibleEffect(type);
    if (nextEffect.type !== 'assign') return;

    if (type === EffectTypes.AssignFromSource) {
      props.onChange(
        updateEffect(props.rule, props.effectIndex, {
          type,
          propertyPath: nextEffect.propertyPath,
          sourcePropertyPath: storedEffect?.sourcePropertyPath || defaultSourcePropertyPath,
          value: null,
        })
      );
      return;
    }

    if (nextEffect.propertyPath === claimAlertEffectPropertyPath) {
      props.onChange(
        updateEffect(props.rule, props.effectIndex, {
          type: EffectTypes.AddOrUpdate,
          propertyPath: nextEffect.propertyPath,
          sourcePropertyPath: undefined,
          value: '',
        })
      );
      return;
    }

    props.onChange(
      updateEffect(props.rule, props.effectIndex, {
        type,
        propertyPath: nextEffect.propertyPath,
        sourcePropertyPath: undefined,
        value:
          nextEffect.propertyPath === selectedEffect.propertyPath && typeof assignValue === 'string'
            ? assignValue
            : (nextEffect.options?.[0]?.id?.toString() ?? defaultAssignValue),
      })
    );
  };

  return (
    <>
      <div style={rowStyle}>
        {showEffectTypeSelector && (
          <SelectInput
            name="type"
            style={inputStyle}
            data={effectTypeOptions.map((type) => ({
              id: type,
              title: translate(type),
            }))}
            value={selectedEffectType}
            disabled={isEffectTypeSelectorDisabled}
            readOnly={isEffectTypeSelectorDisabled}
            onChange={({ type }) => onSelectEffectType(type)}
          />
        )}
        <SelectInput
          name="propertyPath"
          style={inputStyle}
          data={effectPropertyOptions}
          value={selectedEffectPropertyKey}
          error={propertyPathError}
          readOnly={props.readonly || props.isPropertySelectorDisabled}
          disabled={props.readonly || props.isPropertySelectorDisabled}
          onChange={({ propertyPath }) => {
            const nextEffect = availableEffects.find(
              (effect) => effect.type === 'assign' && getAutomationEffectMetadataKey(effect) === propertyPath
            );
            if (!nextEffect || nextEffect.type !== 'assign') return;
            const nextEffectSupportedTypes = getSupportedEffectTypes(props.schema, nextEffect);
            const nextEffectType = nextEffectSupportedTypes.includes(selectedEffectType)
              ? selectedEffectType
              : nextEffectSupportedTypes[0] || EffectTypes.Assign;
            const isNextClaimAlertEffect = nextEffect.propertyPath === claimAlertEffectPropertyPath;

            props.onChange(
              updateEffect(props.rule, props.effectIndex, {
                type: isNextClaimAlertEffect ? EffectTypes.AddOrUpdate : nextEffectType,
                propertyPath: nextEffect.propertyPath,
                sourcePropertyPath:
                  !isNextClaimAlertEffect && nextEffectType === EffectTypes.AssignFromSource
                    ? storedEffect?.sourcePropertyPath || defaultSourcePropertyPath
                    : undefined,
                value:
                  isNextClaimAlertEffect
                    ? ''
                    : nextEffectType === EffectTypes.AssignFromSource
                    ? null
                    : (nextEffect.options?.[0]?.id?.toString() ?? ''),
              })
            );
          }}
        />
        {isClaimAlertEffect ? (
          <Button
            class={ThemeButtonCircle}
            media={<Icon class={claimAlertButtonIcon} />}
            disabled={props.readonly}
            onClick={() => setIsClaimAlertModalOpen(true)}
            htmlElementProps={{ title: claimAlertButtonTitle }}
          />
        ) : selectedEffectType === EffectTypes.AssignFromSource ? (
          <SelectInput
            name="sourcePropertyPath"
            style={inputStyle}
            data={sourcePropertyOptions}
            value={storedEffect?.sourcePropertyPath || ''}
            disabled={props.readonly}
            readOnly={props.readonly}
            onChange={({ sourcePropertyPath }) =>
              props.onChange(
                updateEffect(props.rule, props.effectIndex, {
                  type: EffectTypes.AssignFromSource,
                  propertyPath: selectedEffect.propertyPath,
                  sourcePropertyPath,
                  value: null,
                })
              )
            }
          />
        ) : hasAssignOptions ? (
          <SelectInput
            name="effectValue"
            style={inputStyle}
            data={selectedEffect.options || []}
            value={assignValue || ''}
            error={requiredError}
            disabled={props.readonly}
            readOnly={props.readonly}
            onChange={({ effectValue }) =>
              props.onChange(
                updateEffect(props.rule, props.effectIndex, {
                  type: EffectTypes.Assign,
                  propertyPath: selectedEffect.propertyPath,
                  sourcePropertyPath: undefined,
                  value: effectValue,
                })
              )
            }
          />
        ) : (
          <TextInput
            name="effectValue"
            style={inputStyle}
            value={assignValue || ''}
            error={requiredError}
            readOnly={props.readonly}
            onChange={({ effectValue }) =>
              props.onChange(
                updateEffect(props.rule, props.effectIndex, {
                  type: EffectTypes.Assign,
                  propertyPath: selectedEffect.propertyPath,
                  sourcePropertyPath: undefined,
                  value: effectValue,
                })
              )
            }
          />
        )}
      </div>
      {isClaimAlertModalOpen && (
        <AutomationClaimAlertModal
          value={typeof assignValue === 'string' ? assignValue : undefined}
          onSave={saveClaimAlert}
          onCancel={() => setIsClaimAlertModalOpen(false)}
        />
      )}
    </>
  );
}
